import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { Dropdown } from 'react-bootstrap'
import { PrimaryButton } from '@src/theme/sharedComponents'
import { usePublicSettings } from '@src/hooks/queries/useSettings'
import { useAuth } from '@src/context/AuthContext'
import { authAPI } from '@src/api'
import { IS_DEMO } from '@src/constants/env'
import LoginModal from '@src/features/auth/LoginModal'
import CartDrawer from './CartDrawer'
import { useCart } from '@src/context/CartContext'
import { useNotificationUnreadCount } from '@src/hooks/queries/useNotifications'
import { colors, transitions } from '@src/theme/sharedComponents'

// ─── Styled Components ────────────────────────────────────────────────────────

const StyledNav = styled.nav`
  background: ${colors.primaryDark};
  position: sticky;
  top: 0;
  z-index: 1020;
  transition: ${transitions.default};
  border-bottom: 2px solid ${colors.primary};
  font-size: 0.82rem;

  &.scrolled {
    border-bottom-color: ${colors.accent};
  }

  .navbar-logo-link {
    display: flex;
    align-items: center;
    text-decoration: none;
    padding-right: 0.5rem;

    img { height: 64px; width: auto; filter: brightness(1.15); transition: filter 0.2s ease; }
    &:hover img { filter: brightness(1.35); }
  }

  @media (max-width: 480px) {
    .navbar-logo-link img { height: 48px; }
  }

  .navbar-nav .nav-button {
    background: none;
    border: none;
    color: rgba(255,255,255,0.8);
    font-weight: 700;
    font-size: 0.78rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 0.65rem 1rem;
    transition: color 0.15s;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;

    &:hover { color: ${colors.accent}; }
  }

  .navbar-toggler {
    border: none !important;
    padding: 0.5rem !important;
    color: rgba(255,255,255,0.8) !important;

    .navbar-toggler-icon {
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba%28255,255,255,0.8%29' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e") !important;
    }
  }

  .mobile-controls {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-left: auto;
  }

  @media (max-width: 991.98px) {
    .navbar-logo-link { flex: 1; }

    .navbar-collapse {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      background: ${colors.primaryDark};
      border-top: 1px solid rgba(255,255,255,0.08);
      border-bottom: 2px solid ${colors.accent};
      padding: 0.75rem 0;
      z-index: 1019;
    }

    .navbar-nav .nav-button {
      width: 100%;
      text-align: left;
      padding: 0.7rem 1.25rem !important;
      &:hover { background: rgba(255,255,255,0.05); }
    }

    .mobile-user-section {
      padding-top: 0.75rem;
      margin-top: 0.75rem;
      border-top: 1px solid rgba(255,255,255,0.08);
    }

    .mobile-user-section .dropdown-menu {
      position: static !important;
      transform: none !important;
      box-shadow: none !important;
      border: none !important;
      background: transparent !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    .mobile-user-section .dropdown-item {
      padding: 0.7rem 1.25rem !important;
    }
  }

  /* Dropdown menus */
  .user-dropdown, .more-dropdown {
    .dropdown-menu {
      background: ${colors.primaryDark} !important;
      border: 1px solid rgba(255,255,255,0.12) !important;
      border-radius: 0 !important;
      border-top: 2px solid ${colors.accent} !important;
      box-shadow: 0 8px 24px rgba(0,0,0,0.5) !important;
      padding: 0.25rem 0;
      min-width: 200px;
      margin-top: 2px;
    }

    .dropdown-item {
      font-size: 0.78rem;
      background: none;
      border: none;
      color: rgba(255,255,255,0.75);
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      padding: 0.6rem 1rem !important;
      cursor: pointer;
      transition: color 0.15s, background 0.15s;

      i { margin-right: 0.4rem; opacity: 0.8; }

      &:hover {
        color: ${colors.accent} !important;
        background: rgba(255,255,255,0.05) !important;
      }
    }
  }
`


const IconBtn = styled.button`
  position: relative;
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.75);
  padding: 0.55rem 0.7rem;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s;

  &:hover { color: ${colors.accent}; }
`

const Badge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 16px;
  height: 16px;
  border-radius: 0;
  background: ${colors.accent};
  color: ${colors.primaryDark};
  font-weight: 800;
  font-size: 0.58rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
`

// ─── Component ────────────────────────────────────────────────────────────────

const Navbar: React.FC = () => {
  const [navExpanded, setNavExpanded] = useState(false)
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false)
  const navigate = useNavigate()
  const { data: s } = usePublicSettings()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const { isAuthenticated, user, logout } = useAuth()
  const { isCartOpen, openCart, closeCart, getItemCount } = useCart()
  const [isScrolled, setIsScrolled] = useState(false)
  const [, setLoginMessage] = useState<string | undefined>(undefined)
  const { data: notifCountData } = useNotificationUnreadCount({ enabled: isAuthenticated })
  const unreadCount = notifCountData?.unread_count ?? 0

  const handleNavClick = (path: string) => { navigate(path); setNavExpanded(false) }

  const handleLoginClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setNavExpanded(false)
    searchParams.set('login', 'true')
    setSearchParams(searchParams)
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    setLoginMessage(params.get('message') || undefined)
  }, [location.search])

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleDashboardClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setNavExpanded(false)
    navigate(user?.is_admin ? '/admin-portal' : '/customer-portal')
  }

  const handleLogoutClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    setNavExpanded(false)
    await logout()
  }

  const handleNotificationsClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setNavExpanded(false)
    navigate(user?.is_admin ? '/admin-portal/notifications' : '/customer-portal/notifications')
  }

  const handleDemoSwitch = async () => {
    setNavExpanded(false)
    const targetRole = user?.is_admin ? 'customer' : 'admin'
    try { await authAPI.logout() } catch { /* continue */ }
    window.location.href = `/products?demo=${targetRole}`
  }

  const userMenuItems = user?.is_admin ? (
    <>
      <Dropdown.Item onClick={() => { handleNavClick('/admin-portal/catalog'); setUserDropdownOpen(false) }}><i className="bi bi-grid" />Catalog</Dropdown.Item>
      <Dropdown.Item onClick={() => { handleNavClick('/admin-portal/customers'); setUserDropdownOpen(false) }}><i className="bi bi-person-badge" />Customers</Dropdown.Item>
      <Dropdown.Item onClick={() => { handleNavClick('/admin-portal/orders'); setUserDropdownOpen(false) }}><i className="bi bi-receipt" />Orders</Dropdown.Item>
      <Dropdown.Item onClick={() => { handleNavClick('/admin-portal/storefront'); setUserDropdownOpen(false) }}><i className="bi bi-megaphone" />Storefront</Dropdown.Item>
      <Dropdown.Item onClick={() => { handleNavClick('/admin-portal/settings'); setUserDropdownOpen(false) }}><i className="bi bi-gear" />Settings</Dropdown.Item>
    </>
  ) : (
    <>
      <Dropdown.Item onClick={() => { handleNavClick('/products'); setUserDropdownOpen(false) }}><i className="bi bi-grid" />Products</Dropdown.Item>
      <Dropdown.Item onClick={() => { handleNavClick('/customer-portal/orders'); setUserDropdownOpen(false) }}><i className="bi bi-receipt" />Orders</Dropdown.Item>
      <Dropdown.Item onClick={() => { handleNavClick('/customer-portal/settings'); setUserDropdownOpen(false) }}><i className="bi bi-person-gear" />Settings</Dropdown.Item>
    </>
  )

  const userMenuFooter = (
    <>
      <Dropdown.Divider />
      {IS_DEMO ? (
        <Dropdown.Item onClick={handleDemoSwitch}><i className="bi bi-arrow-left-right" />Switch to {user?.is_admin ? 'Customer' : 'Admin'} View</Dropdown.Item>
      ) : (
        <Dropdown.Item onClick={handleLogoutClick}><i className="bi bi-box-arrow-right" />Logout</Dropdown.Item>
      )}
    </>
  )

  return (
    <>
      <StyledNav className={`navbar navbar-expand-lg py-1 ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container-fluid px-4">

          <a className="navbar-logo-link" href={IS_DEMO ? '/products' : '/'}>
            {s?.navbar_logo && <img src={s.navbar_logo} alt={s.company_name || ''} />}
          </a>

          {/* Mobile controls */}
          <div className="mobile-controls d-lg-none">
            {isAuthenticated && !user?.is_admin && (
              <IconBtn onClick={isCartOpen ? closeCart : openCart} aria-label="Cart">
                <i className="bi bi-cart3" />
                {getItemCount() > 0 && <Badge>{getItemCount() > 99 ? '99+' : getItemCount()}</Badge>}
              </IconBtn>
            )}
            {isAuthenticated && (
              <IconBtn onClick={handleNotificationsClick} aria-label="Notifications">
                <i className="bi bi-bell" />
                {unreadCount > 0 && <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>}
              </IconBtn>
            )}
            <button
              className="navbar-toggler"
              type="button"
              aria-expanded={navExpanded}
              aria-label="Toggle navigation"
              onClick={() => { const next = !navExpanded; setNavExpanded(next); if (next) { setUserDropdownOpen(false); setMoreDropdownOpen(false) } }}
            >
              <span className="navbar-toggler-icon" />
            </button>
          </div>

          <div className={`collapse navbar-collapse ${navExpanded ? 'show' : ''}`} id="navbarNav">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              {IS_DEMO ? (
                <>
                  <li className="nav-item">
                    <button type="button" className="nav-button" onClick={() => handleNavClick('/products')}>
                      <i className="bi bi-box-seam" />Products
                    </button>
                  </li>
                  {isAuthenticated && (
                    <li className="nav-item">
                      <button type="button" className="nav-button" onClick={handleDemoSwitch}>
                        <i className="bi bi-arrow-left-right" />Switch to {user?.is_admin ? 'Customer' : 'Admin'} View
                      </button>
                    </li>
                  )}
                </>
              ) : (
                <>
                  <li className="nav-item">
                    <button type="button" className="nav-button" onClick={() => handleNavClick('/')}><i className="bi bi-house-door" />Home</button>
                  </li>
                  <li className="nav-item">
                    <button type="button" className="nav-button" onClick={() => handleNavClick('/products')}><i className="bi bi-box-seam" />Products</button>
                  </li>
                  <li className="nav-item">
                    <Dropdown className="more-dropdown" show={moreDropdownOpen} onToggle={setMoreDropdownOpen}>
                      <Dropdown.Toggle as="span" bsPrefix="d-inline-block">
                        <button type="button" className="nav-button" onClick={() => setMoreDropdownOpen(o => !o)}>
                          <i className="bi bi-grid" />More <i className="bi bi-chevron-down" style={{ fontSize: '0.65rem' }} />
                        </button>
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => { setMoreDropdownOpen(false); setNavExpanded(false); handleNavClick('/about') }}><i className="bi bi-info-circle" />About</Dropdown.Item>
                        <Dropdown.Item onClick={() => { setMoreDropdownOpen(false); setNavExpanded(false); handleNavClick('/contact') }}><i className="bi bi-envelope" />Contact</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </li>
                </>
              )}
            </ul>

            {/* Mobile user section */}
            <div className="mobile-user-section d-lg-none">
              {isAuthenticated ? (
                <Dropdown className="user-dropdown" show={userDropdownOpen} onToggle={(open) => { setUserDropdownOpen(open); if (open) setNavExpanded(true) }}>
                  <Dropdown.Toggle as="span" bsPrefix="d-block">
                    <PrimaryButton type="button" style={{ width: '100%', justifyContent: 'space-between' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i className="bi bi-person-circle" />{user?.first_name || 'Account'}
                      </span>
                      <i className="bi bi-chevron-down" style={{ fontSize: '0.65rem' }} />
                    </PrimaryButton>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    <Dropdown.Item onClick={handleDashboardClick}><i className="bi bi-speedometer2" />Overview</Dropdown.Item>
                    {userMenuItems}
                    {userMenuFooter}
                  </Dropdown.Menu>
                </Dropdown>
              ) : !IS_DEMO ? (
                <Dropdown className="user-dropdown" show={userDropdownOpen} onToggle={(open) => { setUserDropdownOpen(open); if (open) setNavExpanded(true) }}>
                  <Dropdown.Toggle as="span" bsPrefix="d-block">
                    <PrimaryButton type="button" style={{ width: '100%', justifyContent: 'space-between' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><i className="bi bi-person-circle" />Login</span>
                      <i className="bi bi-chevron-down" style={{ fontSize: '0.65rem' }} />
                    </PrimaryButton>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-100">
                    <Dropdown.Item onClick={handleLoginClick}><i className="bi bi-shield-lock" />Admin Login</Dropdown.Item>
                    <Dropdown.Item onClick={handleLoginClick}><i className="bi bi-person" />Customer Login</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              ) : null}
            </div>
          </div>

          {/* Desktop user section */}
          <div className="d-none d-lg-flex align-items-center gap-2 ms-auto">
            {isAuthenticated && !user?.is_admin && (
              <IconBtn onClick={isCartOpen ? closeCart : openCart} aria-label="Cart">
                <i className="bi bi-cart3" />
                {getItemCount() > 0 && <Badge>{getItemCount() > 99 ? '99+' : getItemCount()}</Badge>}
              </IconBtn>
            )}
            {isAuthenticated && (
              <IconBtn onClick={handleNotificationsClick} aria-label="Notifications">
                <i className="bi bi-bell" />
                {unreadCount > 0 && <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>}
              </IconBtn>
            )}
            {isAuthenticated ? (
              <Dropdown align="end" className="user-dropdown" show={userDropdownOpen} onToggle={setUserDropdownOpen}>
                <Dropdown.Toggle as="span" bsPrefix="d-inline-block">
                  <PrimaryButton type="button">
                    <i className="bi bi-person-circle" />{user?.first_name || 'Account'}
                    <i className="bi bi-chevron-down" style={{ fontSize: '0.65rem', marginLeft: '0.2rem' }} />
                  </PrimaryButton>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handleDashboardClick}><i className="bi bi-speedometer2" />Overview</Dropdown.Item>
                  {userMenuItems}
                  {userMenuFooter}
                </Dropdown.Menu>
              </Dropdown>
            ) : !IS_DEMO ? (
              <Dropdown align="end" className="user-dropdown" show={userDropdownOpen} onToggle={setUserDropdownOpen}>
                <Dropdown.Toggle as="span" bsPrefix="d-inline-block">
                  <PrimaryButton type="button">
                    <i className="bi bi-person-circle" />Login
                    <i className="bi bi-chevron-down" style={{ fontSize: '0.65rem', marginLeft: '0.2rem' }} />
                  </PrimaryButton>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={handleLoginClick}><i className="bi bi-shield-lock" />Admin Login</Dropdown.Item>
                  <Dropdown.Item onClick={handleLoginClick}><i className="bi bi-person" />Customer Login</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : null}
          </div>

        </div>
      </StyledNav>

      <LoginModal />
      <CartDrawer show={isCartOpen} onClose={closeCart} />
    </>
  )
}

export default Navbar
